// Utility functions for data processing and cost calculations

/**
 * Generate cost lookup key for an item
 * @param {Object} item - Item from list.json with rarity, type, energy
 * @returns {string} - Cost lookup key
 */
export const generateCostKey = (item) => {
  if (item.type === 'weapon') {
    return `${item.rarity}_weapon_${item.energy}`;
  } else if (item.type === 'mech') {
    return `${item.rarity}_mech`;
  }
  throw new Error(`Unknown item type: ${item.type}`);
};

/**
 * Find cost data for a specific star and level
 * @param {Array} costs - Array of cost data from mech_upgrade_costs.json
 * @param {string} costKey - Cost lookup key
 * @param {number} star - Star level (1-6)
 * @param {number} level - Level (1-7)
 * @returns {Object|null} - Cost object or null if not found
 */
export const findCostData = (costs, costKey, star, level) => {
  const costEntry = costs.find(entry => 
    entry.star === star && entry.level === level
  );
  
  if (!costEntry) {
    return null;
  }
  
  return costEntry[costKey] || null;
};

/**
 * Calculate total upgrade cost from current to target position
 * @param {Array} costs - Array of cost data
 * @param {string} costKey - Cost lookup key
 * @param {number} currentStar - Current star level
 * @param {number} currentLevel - Current level
 * @param {number} targetStar - Target star level
 * @param {number} targetLevel - Target level
 * @returns {Object} - Total costs {credits, acoins, blueprints}
 */
export const calculateUpgradeCost = (costs, costKey, currentStar, currentLevel, targetStar, targetLevel) => {
  let totalCredits = 0;
  let totalAcoins = 0;
  let totalBlueprints = 0;
  
  let currentStarLevel = currentStar;
  let currentLevelLevel = (currentLevel - 1);
  targetLevel = targetLevel - 1;
 /**
 * Only if target level is 0, then go to last step
 */
  if(targetLevel == 0) {
    targetLevel = 7;
    targetStar = targetStar - 1;
  }
  // console.log("targetLevel", targetLevel);
  // Progress through each upgrade step
  while (currentStarLevel < targetStar || (currentStarLevel === targetStar && currentLevelLevel < targetLevel)) {
    // console.log("currentStarLevel", currentStarLevel);
    // console.log("currentLevelLevel", currentLevelLevel);
    // console.log("targetStar", targetStar);
    // console.log("targetLevel", targetLevel);
    // Move to next level
    currentLevelLevel++;
    
    // If we exceed level 7, move to next star
    if (currentLevelLevel > 7) {
      currentStarLevel++;
      currentLevelLevel = 1;
    }
    
    // Get cost for this upgrade step
    const costData = findCostData(costs, costKey, currentStarLevel, currentLevelLevel);
    
    if (costData) {
      totalCredits += costData.credits || 0;
      totalAcoins += costData.acoins || 0;
      totalBlueprints += costData.blueprints || 0;
    }
    
    // Safety check to prevent infinite loops
    if (currentStarLevel > 6 || (currentStarLevel === 6 && currentLevelLevel > 7)) {
      break;
    }
  }
  
  return {
    credits: totalCredits,
    acoins: totalAcoins,
    blueprints: totalBlueprints
  };
};

/**
 * Validate upgrade parameters
 * @param {number} currentStar - Current star level
 * @param {number} currentLevel - Current level
 * @param {number} targetStar - Target star level
 * @param {number} targetLevel - Target level
 * @returns {Object} - {isValid: boolean, error: string}
 */
export const validateUpgradeParams = (currentStar, currentLevel, targetStar, targetLevel) => {
  // Check if current position is valid
  if (currentStar < 1 || currentStar > 6 || currentLevel < 1 || currentLevel > 7) {
    return {
      isValid: false,
      error: 'Current position must be between 1-6 stars and 1-7 levels'
    };
  }
  
  // Check if target position is valid
  if (targetStar < 1 || targetStar > 6 || targetLevel < 1 || targetLevel > 7) {
    return {
      isValid: false,
      error: 'Target position must be between 1-6 stars and 1-7 levels'
    };
  }
  
  // Check if target is actually an upgrade
  if (targetStar < currentStar || (targetStar === currentStar && targetLevel <= currentLevel)) {
    return {
      isValid: false,
      error: 'Target position must be higher than current position'
    };
  }
  
  return { isValid: true, error: null };
};

/**
 * Calculate upgrade steps with running totals
 * @param {Array} costs - Array of cost data
 * @param {string} costKey - Cost lookup key
 * @param {number} currentStar - Current star level
 * @param {number} currentLevel - Current level
 * @param {number} targetStar - Target star level
 * @param {number} targetLevel - Target level
 * @returns {Array} - Array of upgrade steps with individual and running costs
 */
export const calculateUpgradeSteps = (costs, costKey, currentStar, currentLevel, targetStar, targetLevel) => {
  const steps = [];
  let currentStarLevel = currentStar;
  let currentLevelLevel = (currentLevel - 1);
  
  // Running totals
  let runningCredits = 0;
  let runningAcoins = 0;
  let runningBlueprints = 0;
  targetLevel = targetLevel - 1;
 /**
 * Only if target level is 0, then go to last step
 */
  if(targetLevel == 0) {
    targetLevel = 7;
    targetStar = targetStar - 1;
  }
  let toText;
  while (currentStarLevel < targetStar || (currentStarLevel === targetStar && currentLevelLevel < targetLevel)) {
    currentLevelLevel++;
    
    if (currentLevelLevel > 7) {
      currentStarLevel++;
      currentLevelLevel = 1;
    }
    
    const costData = findCostData(costs, costKey, currentStarLevel, currentLevelLevel);
    toText = `${currentStarLevel}★ L${currentLevelLevel + 1}`;
    if ((currentLevelLevel + 1) == 8) {
      toText = `${currentStarLevel + 1}★ L1`;
    }
    if (costData) {
      // Add to running totals
      runningCredits += costData.credits || 0;
      runningAcoins += costData.acoins || 0;
      runningBlueprints += costData.blueprints || 0;
      
      steps.push({
        from: `${currentStarLevel}★ L${currentLevelLevel}`,
        to: `${toText}`,
        credits: costData.credits || 0,
        acoins: costData.acoins || 0,
        blueprints: costData.blueprints || 0,
        // Running totals
        totalCredits: runningCredits,
        totalAcoins: runningAcoins,
        totalBlueprints: runningBlueprints
      });
    }
    
    // Safety check to prevent infinite loops
    if (currentStarLevel > 6 || (currentStarLevel === 6 && currentLevelLevel > 7)) {
      break;
    }
  }
  
  return steps;
};

/**
 * Format number with commas for display
 * @param {number} num - Number to format
 * @returns {string} - Formatted number
 */
export const formatNumber = (num) => {
  return num.toLocaleString();
};

/**
 * Generate cost lookup key for a mod
 * @param {Object} mod - Mod from mod_list.json with rarity and mod_name
 * @returns {string} - Cost lookup key (e.g. epic_perimeter_ml)
 */
export const generateModCostKey = (mod) => {
  if (!mod || !mod.rarity || !mod.mod_name) {
    throw new Error('Invalid mod object for generateModCostKey');
  }
  return `${mod.rarity}_${mod.mod_name}`;
};

/**
 * Calculate total upgrade cost for a mod from fromLevel to targetLevel
 * @param {Array} costs - Array of cost data from mod_cost.json
 * @param {string} costKey - Cost lookup key (e.g. epic_perimeter_ml)
 * @param {number} fromLevel - Current level (1-5)
 * @param {number} targetLevel - Target level (1-5)
 * @returns {Object} - Total costs {basic_mod_parts, elite_mod_parts}
 */
export const calculateModUpgradeCost = (costs, costKey, fromLevel, targetLevel) => {
  if (!Array.isArray(costs)) {
    throw new Error('Invalid costs data for calculateModUpgradeCost');
  }

  const safeFrom = Number.isFinite(fromLevel) ? fromLevel : 1;
  const safeTarget = Number.isFinite(targetLevel) ? targetLevel : 1;

  if (safeTarget <= safeFrom) {
    return {
      basic_mod_parts: 0,
      elite_mod_parts: 0,
    };
  }

  let totalBasic = 0;
  let totalElite = 0;

  // We assume costs[level] is the cost to go from (level-1) -> level
  const startLevel = Math.max(2, safeFrom + 1);
  for (let level = startLevel; level <= safeTarget; level += 1) {
    const entry = costs.find((c) => c.level === level);
    if (!entry) {
      // If a level is missing, just skip it to avoid breaking the calculator
      // This keeps UX forgiving when data is incomplete.
      continue;
    }

    const levelCost = entry[costKey];
    if (!levelCost) {
      continue;
    }

    totalBasic += levelCost.basic_mod_parts || 0;
    totalElite += levelCost.elite_mod_parts || 0;
  }

  return {
    basic_mod_parts: totalBasic,
    elite_mod_parts: totalElite,
  };
};

/**
 * Calculate per-level upgrade steps for a mod from fromLevel to targetLevel
 * @param {Array} costs - Array of cost data from mod_cost.json
 * @param {string} costKey - Cost lookup key (e.g. epic_perimeter_ml)
 * @param {number} fromLevel - Current level (1-5)
 * @param {number} targetLevel - Target level (1-5)
 * @returns {Array} - Steps with individual and running totals
 */
export const calculateModUpgradeSteps = (costs, costKey, fromLevel, targetLevel) => {
  if (!Array.isArray(costs)) {
    return [];
  }

  const safeFrom = Number.isFinite(fromLevel) ? fromLevel : 1;
  const safeTarget = Number.isFinite(targetLevel) ? targetLevel : 1;

  if (safeTarget <= safeFrom) {
    return [];
  }

  const steps = [];
  let runningBasic = 0;
  let runningElite = 0;

  const startLevel = Math.max(2, safeFrom + 1);
  for (let level = startLevel; level <= safeTarget; level += 1) {
    const entry = costs.find((c) => c.level === level);
    if (!entry) {
      continue;
    }

    const levelCost = entry[costKey];
    const basic = (levelCost && levelCost.basic_mod_parts) || 0;
    const elite = (levelCost && levelCost.elite_mod_parts) || 0;

    runningBasic += basic;
    runningElite += elite;

    steps.push({
      from: `L ${level - 1}`,
      to: `L ${level}`,
      basic_mod_parts: basic,
      elite_mod_parts: elite,
      total_basic_mod_parts: runningBasic,
      total_elite_mod_parts: runningElite,
    });
  }

  return steps;
};

/**
 * Validate parsed player stats before allowing leaderboard submission
 * Enforces:
 * - win_percentage < 100
 * - kills_per_battle <= 45
 *
 * @param {Object} stats - Parsed stats object returned from image processing
 * @returns {{ isValid: boolean, errorKey: string|null }}
 */
export const validateMyStatsForLeaderboard = (stats) => {
  const combatStats = stats && stats.combat_performance_stats ? stats.combat_performance_stats : null;

  if (!combatStats) {
    return {
      isValid: false,
      errorKey: 'stats.invalidStatsGeneric',
    };
  }

  const winPercentage = Number(combatStats.win_percentage);
  const killsPerBattle = Number(combatStats.kills_per_battle);
  const totalWins = Number(combatStats.total_wins);
  const totalBattles = Number(combatStats.total_battles);
  const totalKills = Number(combatStats.total_kills);
  const highestDamage = Number(combatStats.highest_damage);
  const inventoryPower = Number(stats.inventory_power);

  if (!Number.isFinite(winPercentage) || !Number.isFinite(killsPerBattle)) {
    return {
      isValid: false,
      errorKey: 'stats.invalidStatsGeneric',
    };
  }

  // Win % must be strictly less than 100
  if (winPercentage >= 100) {
    return {
      isValid: false,
      errorKey: 'stats.invalidWinPercent',
    };
  }

  // Kills per battle must not exceed 45
  if (killsPerBattle > 45) {
    return {
      isValid: false,
      errorKey: 'stats.invalidKillsPerBattle',
    };
  }

  // Wins cannot exceed total battles
  if (totalWins > totalBattles) {
    return {
      isValid: false,
      errorKey: 'stats.invalidWinsExceedBattles',
    };
  }

  // Win percentage must roughly match total_wins / total_battles (allow 2% tolerance for rounding)
  if (totalBattles > 0) {
    const expectedWinPct = (totalWins / totalBattles) * 100;
    if (Math.abs(expectedWinPct - winPercentage) > 2) {
      return {
        isValid: false,
        errorKey: 'stats.invalidWinPercentMismatch',
      };
    }
  }

  // Kills per battle must roughly match total_kills / total_battles (allow 0.5 tolerance)
  if (totalBattles > 0 && totalKills > 0) {
    const expectedKpb = totalKills / totalBattles;
    if (Math.abs(expectedKpb - killsPerBattle) > 0.5) {
      return {
        isValid: false,
        errorKey: 'stats.invalidKillsPerBattleMismatch',
      };
    }
  }

  // Inventory power sanity cap (current max in game is well under 100M)
  if (inventoryPower > 500000) {
    return {
      isValid: false,
      errorKey: 'stats.invalidInventoryPower',
    };
  }

  return {
    isValid: true,
    errorKey: null,
  };
};